import { useEffect } from "react";
import { SITE_URL, DEFAULT_LANG, LANGS, OG_LOCALE, buildUrl } from "./siteConfig";

const ATTR = "data-cf-seo";

function removeOldTags() {
  document.querySelectorAll(`[${ATTR}="1"]`).forEach((el) => el.remove());
  // Also remove static duplicates from index.html to prevent double tags
  const staticSelectors = [
    'meta[name="description"]:not([data-cf-seo])',
    'meta[name="robots"]:not([data-cf-seo])',
    'meta[property^="og:"]:not([data-cf-seo])',
    'meta[property^="article:"]:not([data-cf-seo])',
    'meta[name^="twitter:"]:not([data-cf-seo])',
    'link[rel="canonical"]:not([data-cf-seo])',
    'link[rel="alternate"][hreflang]:not([data-cf-seo])',
  ];
  staticSelectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => el.remove());
  });
}

function addTag(tag, attrs) {
  const el = document.createElement(tag);
  el.setAttribute(ATTR, "1");
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "textContent") el.textContent = v;
    else el.setAttribute(k, v);
  });
  document.head.appendChild(el);
  return el;
}

export default function Seo({ path, lang, title, description, image, jsonLd, noindex, ogType, articleMeta }) {
  useEffect(() => {
    removeOldTags();

    const effectiveLang = lang || DEFAULT_LANG;
    const canonicalUrl = buildUrl(path || "/", effectiveLang);
    const ogImage = image || `${SITE_URL}/og-default.jpg`;

    document.title = title;
    document.documentElement.setAttribute("lang", effectiveLang);

    addTag("meta", { name: "description", content: description });
    addTag("meta", { name: "robots", content: noindex ? "noindex, follow" : "index, follow" });
    addTag("link", { rel: "canonical", href: canonicalUrl });

    // Open Graph
    addTag("meta", { property: "og:type", content: ogType || "website" });
    addTag("meta", { property: "og:title", content: title });
    addTag("meta", { property: "og:description", content: description });
    addTag("meta", { property: "og:url", content: canonicalUrl });
    addTag("meta", { property: "og:site_name", content: "Clean-Force BV" });
    addTag("meta", { property: "og:image", content: ogImage });
    addTag("meta", { property: "og:locale", content: OG_LOCALE[effectiveLang] });
    LANGS.filter((l) => l !== effectiveLang).forEach((l) => {
      addTag("meta", { property: "og:locale:alternate", content: OG_LOCALE[l] });
    });

    // Article meta (published_time, modified_time)
    if (articleMeta) {
      if (articleMeta.published) addTag("meta", { property: "article:published_time", content: articleMeta.published });
      if (articleMeta.modified) addTag("meta", { property: "article:modified_time", content: articleMeta.modified });
      if (articleMeta.author) addTag("meta", { property: "article:author", content: articleMeta.author });
    }

    // Twitter
    addTag("meta", { name: "twitter:card", content: "summary_large_image" });
    addTag("meta", { name: "twitter:title", content: title });
    addTag("meta", { name: "twitter:description", content: description });
    addTag("meta", { name: "twitter:image", content: ogImage });

    // Hreflang alternates
    LANGS.forEach((l) => {
      addTag("link", { rel: "alternate", hreflang: l, href: buildUrl(path || "/", l) });
    });
    addTag("link", { rel: "alternate", hreflang: "x-default", href: buildUrl(path || "/", DEFAULT_LANG) });

    // JSON-LD (supports single object or array of objects)
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        if (item) addTag("script", { type: "application/ld+json", textContent: JSON.stringify(item) });
      });
    }

    return () => removeOldTags();
  }, [path, lang, title, description, image, jsonLd, noindex, ogType, articleMeta]);

  return null;
}
